import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Breadcrumbs,
  Link,
  Divider,
  Tooltip,
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { SearchBar } from './SearchBar';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface PageAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  disabled?: boolean;
  tooltip?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: PageAction[];
  // Search props
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearch?: () => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  // Layout
  divider?: boolean;
  sx?: any;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs,
  actions,
  searchValue,
  onSearchChange,
  onSearch,
  searchPlaceholder,
  showSearch = false,
  divider = true,
  sx,
}) => {
  return (
    <Box sx={{ mb: 3, ...sx }}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 1 }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return isLast ? (
              <Typography key={index} color="text.primary">
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={index}
                color="inherit"
                href={crumb.href}
                onClick={crumb.onClick}
                underline="hover"
                sx={{ cursor: crumb.onClick ? 'pointer' : 'default' }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}
      
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: divider ? 2 : 0 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom={!!subtitle}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          {showSearch && onSearchChange && (
            <SearchBar
              value={searchValue || ''}
              onChange={onSearchChange}
              onSearch={onSearch}
              placeholder={searchPlaceholder}
              size="small"
              fullWidth={false}
              sx={{ minWidth: 250 }}
            />
          )}
          
          {actions && actions.map((action, index) => {
            const button = (
              <Button
                key={index}
                variant={action.variant || 'contained'}
                color={action.color || 'primary'}
                startIcon={action.icon}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.label}
              </Button>
            );
            
            return action.tooltip ? (
              <Tooltip key={index} title={action.tooltip}>
                <span>{button}</span>
              </Tooltip>
            ) : button;
          })}
        </Stack>
      </Stack>
      
      {divider && <Divider sx={{ mb: 3 }} />}
    </Box>
  );
};
